/**
 * Amplifier profiles -- group 02 (British and northern-European mainstream
 * high fidelity).
 *
 * Most of this group is nominally class AB, so the differentiation has to come
 * from the things that actually change the transfer function: how much of the
 * output-stage error is cancelled and by what mechanism, how stiff the supply
 * is under sustained load, how much source impedance the loudspeaker sees, and
 * where the bandwidth corners sit. Two entries break out of class AB entirely
 * -- a Purifi switching design and a nuvistor hybrid -- and those are modelled
 * with their own nonlinearity kinds.
 */

import type { AmpProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeAmp, usd } from '../kit';

export const AMPS_02: AmpProfile[] = [
  makeAmp({
    mfr: 'NAD',
    model: 'M33',
    price: usd(4999, 'MSRP', 2020),
    year: 2020,
    country: 'Canada',
    topology: 'class-d',
    form: 'integrated',
    devices: 'Purifi Eigentakt output modules with a linear front end',
    power: { watts8: 200, watts4: 200, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 34,
    thdPercent: 0.0008,
    snrDb: 120,
    bandwidth: [3, 50000],
    dampingFactor: 800,
    feedbackDb: 34,
    crosstalkDb: -110,
    harmonicShape: HARMONIC_SHAPES.classD,
    nonlinear: { kind: 'class-d-pwm', amount: 0.22, carrierHz: 600000, headroomDb: 7, asymmetry: 0.05, oversample: 4 },
    dynamics: { sagDb: 0.1, sagTimeMs: 20, recoveryMs: 50, thermalDb: 0.05, slewRateVus: 28, headroomDb: 0.7 },
    clipping: { kind: 'hard', kneeDb: 0.3, stickiness: 0.04 },
    balancedInput: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5b6cff',
    tags: ['purifi', 'eigentakt', 'streaming-integrated', 'high-damping'],
    notes:
      'The Eigentakt loop encloses the output filter, which is the whole point: in an ordinary class D the LC filter sits outside the feedback and its impedance interacts with the load, so response and distortion both move with the speaker. Here output impedance stays near-constant across the band and rated power into 4 ohms equals that into 8, so the model applies almost no load-dependent response error and essentially no supply sag. Clipping is modelled as hard with a very narrow knee, because a switching stage runs out of modulation index abruptly rather than compressing.',
    provenanceNote:
      'Rated power into 8 and 4 ohms and signal-to-noise ratio are NAD published figures. The THD value used here is the datasheet order of magnitude for the Eigentakt module rather than an exact quotation, and damping factor, bandwidth corners and switching carrier frequency are ESTIMATED from the module family. Nonlinear and clipping behaviour are MODELLED.',
  }),

  makeAmp({
    mfr: 'Hegel Music Systems',
    model: 'H390',
    price: usd(6000, 'MSRP', 2019),
    year: 2019,
    country: 'Norway',
    topology: 'solid-state-ab',
    form: 'integrated',
    devices: 'Bipolar output stage with Hegel SoundEngine 2 feed-forward error correction',
    power: { watts8: 250, watts4: 400, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 37,
    thdPercent: 0.005,
    snrDb: 100,
    bandwidth: [5, 100000],
    dampingFactor: 4000,
    feedbackDb: 12,
    crosstalkDb: -100,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'class-b-crossover', amount: 0.2, crossoverWidth: 0.0015, headroomDb: 11, asymmetry: 0.1, oversample: 4 },
    dynamics: { sagDb: 0.3, sagTimeMs: 55, recoveryMs: 150, thermalDb: 0.1, slewRateVus: 55, headroomDb: 2.0 },
    clipping: { kind: 'asymmetric', kneeDb: 2.0, stickiness: 0.07 },
    balancedInput: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4f8ea8',
    tags: ['error-correction', 'soundengine', 'very-high-damping', 'streaming-integrated'],
    notes:
      'SoundEngine 2 senses the output-stage error and cancels it feed-forward rather than swamping it with a large global loop. The modelled consequence is a residual that stays low-order and roughly flat with frequency instead of rising with frequency as loop gain falls off, and a crossover notch narrow enough that low-level distortion behaves more like a Class A design than a class AB one. The claimed damping factor of several thousand means the loudspeaker impedance curve prints essentially nothing onto the frequency response, which is the opposite of the Naim and Roksan entries here.',
    provenanceNote:
      'Rated power, THD, signal-to-noise ratio, crosstalk and damping factor are Hegel published specifications. Feedback depth, bandwidth corners, sag and the crossover-width parameter are ESTIMATED from the described topology; Hegel does not publish them.',
  }),

  makeAmp({
    mfr: 'Naim Audio',
    model: 'NAP 250 DR',
    price: usd(6995, 'APPROXIMATE', 2018, 'Approximately USD 6,995 in the United States before it was superseded. APPROXIMATE.'),
    year: 2015,
    country: 'United Kingdom',
    topology: 'solid-state-ab',
    form: 'stereo-power',
    devices: 'Complementary bipolar output, discrete DR voltage regulators, series output network',
    power: { watts8: 80, watts4: 130, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 29,
    thdPercent: 0.03,
    snrDb: 96,
    bandwidth: [3, 60000],
    outputImpedanceOhms: 0.1,
    feedbackDb: 20,
    crosstalkDb: -80,
    harmonicShape: HARMONIC_SHAPES.classAB,
    nonlinear: { kind: 'class-b-crossover', amount: 0.4, crossoverWidth: 0.0035, headroomDb: 10, asymmetry: 0.2, oversample: 4 },
    dynamics: { sagDb: 0.25, sagTimeMs: 40, recoveryMs: 90, slewRateVus: 45, headroomDb: 2.2 },
    clipping: { kind: 'asymmetric', kneeDb: 2.2, stickiness: 0.1 },
    confidence: 'ESTIMATED',
    accent: '#3f9a4a',
    tags: ['regulated-supply', 'series-output-network', 'moderate-damping'],
    notes:
      'The design emphasis is the supply, not the output stage: heavily regulated discrete rails hold their voltage under sustained demand, so the modelled sag is small and its recovery fast even though the amplifier is only rated at 80 watts. Against that, a series inductor and resistor in the output raise source impedance to around a tenth of an ohm, so a loudspeaker with a swinging impedance curve will print a visible amount of that curve onto the response -- roughly a decibel of variation across a 4-to-16 ohm swing. Crossover residue is left visible in the model because the feedback factor is moderate rather than large.',
    provenanceNote:
      'Rated power into 8 and 4 ohms is a Naim published figure. Naim does not publish THD, signal-to-noise ratio, damping factor or bandwidth to the resolution this model needs, so those four values are ESTIMATED from the topology and the known series output network and are explicitly not quotations.',
  }),

  makeAmp({
    mfr: 'Linn',
    model: 'Klimax Twin',
    price: usd(13000, 'APPROXIMATE', 2018, 'Approximately USD 13,000; Linn pricing varies by market and revision. APPROXIMATE.'),
    year: 2007,
    country: 'United Kingdom',
    topology: 'solid-state-ab',
    form: 'stereo-power',
    devices: 'Linn Chakra output stage (linear voltage amplifier plus current-dumping devices), switch-mode supply',
    power: { watts8: 100, watts4: 175 },
    gainDb: 29,
    thdPercent: 0.008,
    snrDb: 105,
    bandwidth: [2, 90000],
    dampingFactor: 250,
    feedbackDb: 22,
    crosstalkDb: -95,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'class-b-crossover', amount: 0.25, crossoverWidth: 0.0012, headroomDb: 11, asymmetry: 0.12, oversample: 4 },
    dynamics: { sagDb: 0.15, sagTimeMs: 25, recoveryMs: 70, thermalDb: 0.08, slewRateVus: 55, headroomDb: 1.6 },
    clipping: { kind: 'asymmetric', kneeDb: 1.8, stickiness: 0.06 },
    confidence: 'ESTIMATED',
    accent: '#c0563f',
    tags: ['chakra', 'current-dumping', 'switch-mode-supply'],
    notes:
      'Chakra splits the job: a small linear amplifier defines the output voltage accurately while high-current devices supply the bulk of the load current, a current-dumping-derived arrangement in which the dumper stage never has to be linear. The modelled crossover notch is therefore much narrower than a conventional class AB of the same rating, which shows up as low-level distortion that stays low-order rather than breaking into high-order products at small signal. A high-frequency switching supply replenishes its reservoir hundreds of times more often than a mains-frequency one, so rail sag is modelled as slight and fast-recovering.',
    provenanceNote:
      'The Chakra output topology and the switch-mode supply are from Linn published descriptions. Rated power, THD, signal-to-noise ratio, damping factor and bandwidth are ESTIMATED; Linn publishes very little numerical specification for its power amplifiers, so none of those figures should be read as quoted.',
  }),

  makeAmp({
    mfr: 'Cyrus Audio',
    model: 'i9-XR',
    price: usd(2995, 'APPROXIMATE', 2019, 'Approximately USD 2,995. APPROXIMATE.'),
    year: 2018,
    country: 'United Kingdom',
    topology: 'solid-state-ab',
    form: 'integrated',
    devices: 'Complementary bipolar output in a half-width cast alloy chassis; external PSX-R2 regulated supply optional',
    power: { watts8: 75, watts4: 110 },
    gainDb: 36,
    thdPercent: 0.02,
    snrDb: 95,
    bandwidth: [8, 50000],
    outputImpedanceOhms: 0.09,
    feedbackDb: 26,
    crosstalkDb: -85,
    harmonicShape: HARMONIC_SHAPES.classAB,
    nonlinear: { kind: 'class-b-crossover', amount: 0.5, crossoverWidth: 0.0045, headroomDb: 9, asymmetry: 0.18, oversample: 4 },
    dynamics: { sagDb: 0.9, sagTimeMs: 90, recoveryMs: 260, thermalDb: 0.15, slewRateVus: 35, headroomDb: 1.2 },
    clipping: { kind: 'asymmetric', kneeDb: 2.8, stickiness: 0.14 },
    confidence: 'ESTIMATED',
    accent: '#6f7a86',
    tags: ['half-width', 'outboard-psu-upgrade', 'soft-supply'],
    notes:
      'A compact chassis constrains transformer and reservoir size, so the distinguishing behaviour of this profile is supply sag rather than static distortion: sustained low-frequency demand pulls the rails down by close to a decibel and takes a couple of hundred milliseconds to recover, which is precisely the deficiency the optional outboard regulated supply exists to remove. Static distortion is ordinary class AB in structure, with a crossover notch wide enough to remain visible at low listening levels. Bandwidth corners are placed conservatively, so the top octave is not extended the way the Chord and Hegel entries are.',
    provenanceNote:
      'Cyrus rates this model into 6 ohms; the 8-ohm and 4-ohm figures used here are inferred from that rating and are not quotations. THD, signal-to-noise ratio, bandwidth, feedback depth and output impedance are ESTIMATED from the topology. The sag behaviour is MODELLED from the documented outboard-supply upgrade path rather than from a measurement.',
  }),

  makeAmp({
    mfr: 'Chord Electronics',
    model: 'Ultima 6',
    price: usd(12500, 'APPROXIMATE', 2021, 'Approximately USD 12,500. APPROXIMATE.'),
    year: 2020,
    country: 'United Kingdom',
    topology: 'solid-state-ab',
    form: 'stereo-power',
    devices: 'Dual feed-forward error-correction output stage, high-frequency switch-mode supply',
    power: { watts8: 180, watts4: 300 },
    gainDb: 27,
    thdPercent: 0.002,
    snrDb: 108,
    bandwidth: [1, 100000],
    dampingFactor: 500,
    feedbackDb: 14,
    crosstalkDb: -100,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'class-b-crossover', amount: 0.18, crossoverWidth: 0.001, headroomDb: 12, asymmetry: 0.1, oversample: 4 },
    dynamics: { sagDb: 0.06, sagTimeMs: 15, recoveryMs: 40, thermalDb: 0.06, slewRateVus: 70, headroomDb: 2.4 },
    clipping: { kind: 'asymmetric', kneeDb: 1.6, stickiness: 0.05 },
    balancedInput: true,
    confidence: 'ESTIMATED',
    accent: '#d8c07a',
    tags: ['feed-forward-correction', 'hf-switching-supply', 'wide-bandwidth'],
    notes:
      'A switching supply running in the hundreds of kilohertz replenishes its energy store roughly four orders of magnitude more often than a mains-frequency transformer bank, so this profile carries the smallest sag term of any linear-output amplifier in the group and the fastest recovery. The output stage is held at high standing bias with a feed-forward error-correction loop, so the residual is low-order and largely level-independent rather than crossover-dominated, and it does not climb with frequency. Bandwidth is modelled very wide at both ends, with a low-frequency corner near 1 Hz, so there is no audible phase shift left in the bass.',
    provenanceNote:
      'Supply architecture and the feed-forward error-correction output stage are from Chord Electronics published material, and rated power is approximately the published 8-ohm figure. THD, signal-to-noise ratio, damping factor and bandwidth corners are ESTIMATED from the topology and are not quotations.',
  }),

  makeAmp({
    mfr: 'Musical Fidelity',
    model: 'Nu-Vista 800',
    price: usd(12000, 'APPROXIMATE', 2015, 'Approximately USD 12,000 at introduction. APPROXIMATE.'),
    year: 2014,
    country: 'United Kingdom',
    topology: 'hybrid',
    form: 'integrated',
    devices: 'Nuvistor miniature triodes in the input and driver stages ahead of a bipolar output bank',
    power: { watts8: 330, watts4: 500 },
    gainDb: 38,
    thdPercent: 0.008,
    snrDb: 110,
    bandwidth: [5, 80000],
    dampingFactor: 180,
    feedbackDb: 24,
    crosstalkDb: -90,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'triode', amount: 0.35, asymmetry: 0.5, bias: 0.55, headroomDb: 14, oversample: 4 },
    dynamics: { sagDb: 0.2, sagTimeMs: 50, recoveryMs: 130, thermalDb: 0.15, thermalTimeMs: 9000, slewRateVus: 50, headroomDb: 2.6 },
    clipping: { kind: 'asymmetric', kneeDb: 3.5, stickiness: 0.12 },
    balancedInput: true,
    confidence: 'ESTIMATED',
    accent: '#c9a24d',
    tags: ['nuvistor', 'hybrid-front-end', 'high-current', 'large-supply'],
    notes:
      'The nuvistor is a miniature metal-envelope triode run at modest voltage in the small-signal stages; the output is a large bank of bipolar devices on a conventional linear supply. The result modelled here is a transfer curve that is asymmetric and second-harmonic-weighted in shape, like a tube stage, but two orders of magnitude lower in magnitude than an output-stage tube amplifier because the distortion is generated at line level and then reduced by the loop around the power stage. Because the output devices are solid state, damping factor and current delivery stay high, so unlike a real tube amplifier this profile does not let the loudspeaker impedance curve into the response.',
    provenanceNote:
      'Topology, the nuvistor front end and rated power are from Musical Fidelity published material. THD, signal-to-noise ratio, damping factor and bandwidth used here are ESTIMATED for the topology and are not quoted from the manufacturer sheet. The nonlinear model is MODELLED as a low-amount triode curve to represent a small-signal tube stage inside a solid-state loop.',
  }),

  makeAmp({
    mfr: 'Roksan',
    model: 'Caspian M2 Integrated Amplifier',
    price: usd(2500, 'APPROXIMATE', 2015, 'Approximately USD 2,500. APPROXIMATE.'),
    year: 2010,
    country: 'United Kingdom',
    topology: 'solid-state-ab',
    form: 'integrated',
    devices: 'Complementary bipolar output, large toroidal transformer with separate secondaries per channel',
    power: { watts8: 85, watts4: 125 },
    gainDb: 35,
    thdPercent: 0.02,
    snrDb: 93,
    bandwidth: [10, 40000],
    outputImpedanceOhms: 0.12,
    feedbackDb: 22,
    crosstalkDb: -80,
    harmonicShape: HARMONIC_SHAPES.classAB,
    nonlinear: { kind: 'class-b-crossover', amount: 0.45, crossoverWidth: 0.004, headroomDb: 10, asymmetry: 0.22, oversample: 4 },
    dynamics: { sagDb: 0.5, sagTimeMs: 70, recoveryMs: 180, thermalDb: 0.12, slewRateVus: 30, headroomDb: 1.5 },
    clipping: { kind: 'asymmetric', kneeDb: 3.0, stickiness: 0.16 },
    confidence: 'ESTIMATED',
    accent: '#7d5fa8',
    tags: ['separate-rails', 'moderate-feedback', 'narrow-bandwidth'],
    notes:
      'The most consequential number in this profile is the 0.12 ohm output impedance: with a damping factor of only about 67, a loudspeaker whose impedance swings between 4 and 16 ohms will shift this amplifier response by more than a decibel across the band, so pairing changes the sound audibly. Bandwidth is set the narrowest in the group at both ends, which removes ultrasonic energy but leaves a small phase shift inside the audio band. Separate transformer secondaries per channel keep crosstalk at a moderate rather than exceptional level while allowing each channel to sag independently under asymmetric programme.',
    provenanceNote:
      'Rated power is approximately the published 8-ohm figure. THD, signal-to-noise ratio, bandwidth, output impedance, feedback depth and sag behaviour are all ESTIMATED from the topology and the known supply arrangement; Roksan does not publish these to the resolution the model requires, so none are quotations.',
  }),
];
